import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getAllShots } from "service";
import { useHfBranch } from "./useHfBranch";
import { useParams } from "react-router-dom";
import { useState } from "react";

export interface IAllShotsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IShotResult[];
}

export interface IShotResult {
  id: number;
  client: string;
  vaccine_name: string;
  vaccine_brand: string;
  vaccinated_on: string;
  vaccinated_by: string;
  next_vaccination_date: string;
  branch: string;
  shot_status: "administered" | "upcoming" | "due";
}

export interface IShotsFilteredData {
  branch_name: string;
  client: string;
  client_ino_id: string;
  client_id?: number;
  statusType: string;
  vaccinated_on: string;
  vaccination?: number;
  vaccine_batch_number: string;
  vaccine_name: string;
}

export const useShots = (statusType = "") => {
  const { hfId } = useParams();
  const { branch } = useHfBranch();
  const queryClient = useQueryClient();

  const [size, setSize] = useState(10);
  const [filters, setFilters] = useState<IShotsFilteredData>({
    branch_name: "",
    client: "",
    client_ino_id: "",
    client_id: undefined,
    statusType: "",
    vaccinated_on: "",
    vaccination: undefined,
    vaccine_batch_number: "",
    vaccine_name: ""
  });

  const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });

  const {
    data: allShotsData,
    isPending: totalShotsDataLoading,
    error,
    isPlaceholderData,
    refetch
  } = useQuery<IAllShotsResponse>({
    queryKey: ["all-vaccinations", hfId, size, statusType, pagination.current, pagination.pageSize, filters],
    queryFn: async () => {
      if (!branch) return { results: [], count: 0, next: null, previous: null }
      const response = await getAllShots(
        branch.hfId,
        pagination.current,
        pagination.pageSize,
        {
          branch_name: filters.branch_name,
          client: filters.client,
          client_ino_id: filters.client_ino_id,
          client_id: filters.client_id || undefined,
          statusType: filters.statusType || statusType,
          vaccinated_on: filters.vaccinated_on,
          vaccination: filters.vaccination || undefined,
          vaccine_batch_number: filters.vaccine_batch_number,
          vaccine_name: filters.vaccine_name,
        }
      );
      return response;
    },
    enabled: !!branch,
    staleTime: 0,
  });

  const handlePageChange = (page: number, pageSize?: number) => {
    const newPage = Math.max(1, page);
    setPagination(prev => ({
      current: newPage,
      pageSize: pageSize ?? prev.pageSize,
    }));
    setTimeout(() => refetch(), 0);
  };

  // Update filters function
  const updateFilters = (newFilters: any) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
    // Force refetch with the new filters
    setTimeout(() => refetch(), 0);
  };

  const {
    data: allShots,
    isPending,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery<IAllShotsResponse>({
    initialPageParam: 1,
    queryKey: ["all-shots", hfId, size, statusType],
    queryFn: async ({ pageParam = 1 }) => {
      if (!branch) return;
      const response = await getAllShots(
        branch.hfId,
        pageParam,
        size
      );
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;

      const url = new URL(lastPage.next);
      return Number(url.searchParams.get("page")) || undefined;
    },
    getPreviousPageParam: (_, allPages) => {
      const firstLoadedPage = allPages[allPages.length - 1];

      if (!firstLoadedPage.previous) {
        // console.log("No previous page found.");
        return undefined;
      }

      try {
        const url = new URL(firstLoadedPage.previous);
        const pageParam = Number(url.searchParams.get("page"));

        return isNaN(pageParam) ? undefined : pageParam;
      } catch (error) {
        console.error("Error parsing previous page URL:", error);
        return undefined;
      }
    },
    enabled: !!branch,
  });

  return {
    allShots,
    isPending,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    pagination,
    allShotsData,
    totalShotsDataLoading,
    isPlaceholderData,
    handlePageChange,
    setFilters,
    filters,
    updateFilters
  };
};
